/**
 * Page tracking hook for analytics
 *
 * Automatically tracks page views on route changes.
 * Place this hook in a component that has access to react-router's useLocation.
 *
 * Usage:
 *   function AppRoutes() {
 *     usePageTracking();
 *     return <Routes>...</Routes>;
 *   }
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analytics } from '@/lib/analytics';
import { EVENTS } from '@/lib/analytics/config';

const SCROLL_DEPTH_THRESHOLDS = [25, 50, 75, 100];

// Map routes to readable page titles
const PAGE_TITLES: Record<string, string> = {
  '/': 'Home',
  '/login': 'Login',
  '/pricing': 'Pricing',
  '/how-it-works': 'How It Works',
  '/security': 'Security',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms of Service',
  '/stories': 'Customer Stories',
  '/features': 'Features',
  '/guides': 'Guides',
  '/tools': 'Tools',
  '/research': 'Research Hub',
  '/contact': 'Contact Us',
  '/quiz': 'Quiz',
  '/playbook': 'Playbook',
  '/audit': 'Audit',
  '/developer-analytics': 'Developer Analytics',
  '/bus-factor': 'Bus Factor Analysis',
  '/code-hotspots': 'Code Hotspots',
  '/engineering-code-intelligence': 'Engineering Code Intelligence',
  '/sample-report': 'Sample Report',
  '/github-analytics': 'GitHub Analytics',
  '/benchmark-report': 'Benchmark Report',
  '/onboarding': 'Onboarding',
  '/onboarding/prerequisites': 'Prerequisites',
  '/onboarding/orgs': 'Select Organization',
  '/onboarding/check-access': 'Check Access',
  '/onboarding/select': 'Select Organization',
  '/onboarding/select-repos': 'Select Repositories',
  '/onboarding/syncing': 'Syncing',
  '/onboarding/personalize': 'Personalize',
  '/onboarding/insights': 'First Insights',
  '/dashboard': 'Dashboard',
  '/pull-requests': 'Pull Requests',
  '/release-report': 'Release Report',
  '/deployment-analytics': 'Deployment Analytics',
  '/repositories': 'Repositories',
  '/repositories/compare': 'Compare Repositories',
  '/developers': 'Developer Profiles',
  '/developer-league': 'Developer League',
  '/awards': 'Awards',
  '/test-champions': 'Test Champions',
  '/polyglot-leaderboard': 'Polyglot Leaderboard',
  '/performance-insights': 'Performance Insights',
  '/alerts': 'Alerts',
  '/notification-history': 'Notification History',
  '/review-network': 'Review Network',
  '/file-hotspots': 'File Hotspots',
  '/code-maturity': 'Code Maturity',
  '/knowledge-silos': 'Knowledge Silos',
  '/risky-changes': 'Risky Changes',
  '/review-insights': 'Code Review Insights',
  '/executive': 'Executive Summary',
  '/team': 'Access',
  '/year-in-review': 'Year in Review',
  '/benchmarks': 'Benchmarks',
  '/organizations': 'Organizations',
  '/billing': 'Billing Settings',
  '/admin': 'Admin Dashboard',
  '/admin/growth': 'Admin Growth',
  '/admin/organizations': 'Admin Organizations',
  '/admin/issues': 'Admin Issues',
  '/admin/gift': 'Admin Gift',
  '/admin/audit-log': 'Admin Audit Log',
  '/admin/cancellation-feedback': 'Admin Cancellation Feedback',
};

/**
 * Get page title from path, handling dynamic routes
 */
function getPageTitle(pathname: string): string {
  // Check for exact match first
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }

  // Handle dynamic routes
  if (pathname.startsWith('/repositories/') && pathname !== '/repositories/compare') {
    return 'Repository Details';
  }
  if (pathname.startsWith('/developers/')) {
    return 'Developer Details';
  }
  if (pathname.startsWith('/stories/')) {
    return 'Customer Story';
  }
  if (pathname.startsWith('/share/')) {
    return 'Shared View';
  }
  if (pathname.startsWith('/team/')) {
    return 'Team Profile';
  }
  if (pathname.startsWith('/admin/organizations/')) {
    return 'Admin Organization Details';
  }
  if (pathname.startsWith('/guides/')) {
    return 'Guide';
  }
  if (pathname.startsWith('/tools/') && pathname.endsWith('/embed')) {
    return 'Tool Embed';
  }
  if (pathname.startsWith('/tools/')) {
    return 'Tool';
  }
  if (pathname.startsWith('/research/')) {
    return 'Research';
  }
  if (pathname.startsWith('/features/')) {
    return 'Feature';
  }
  if (pathname.startsWith('/onboarding')) {
    return 'Onboarding';
  }

  // Default to document title or path
  return document.title || pathname;
}

export function usePageTracking(): void {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    // Avoid tracking the same page twice (e.g., on initial mount)
    if (previousPathRef.current === location.pathname) {
      return;
    }

    previousPathRef.current = location.pathname;

    const title = getPageTitle(location.pathname);

    // Set document.title BEFORE firing the GA4 event.
    // GA4's "Page title and screen name" dimension reads from document.title
    // at event time, not from the page_title event parameter. Helmet updates
    // document.title asynchronously (after render), so it's still stale when
    // this effect runs. Setting it here ensures GA4 picks up the correct title.
    // Helmet will overwrite this with the full SEO title shortly after.
    document.title = title;

    analytics.page(location.pathname, title);
  }, [location.pathname]);

  // Scroll-depth tracking: fire once per 25/50/75/100% threshold per page.
  useEffect(() => {
    const fired = new Set<number>();

    const handleScroll = () => {
      const docHeight = document.documentElement.scrollHeight;
      if (docHeight <= 0) return;
      // Percentage of the page seen (viewport bottom relative to full height).
      const percent = ((window.scrollY + window.innerHeight) / docHeight) * 100;

      for (const threshold of SCROLL_DEPTH_THRESHOLDS) {
        if (percent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          analytics.track({
            name: EVENTS.SCROLL_DEPTH,
            params: { depth: threshold, path: location.pathname },
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);
}

export default usePageTracking;
